import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';

// ---- routing ----

/** Current path, updated on navigate() and back/forward. */
export function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const update = () => setPath(location.pathname);
    addEventListener('popstate', update);
    addEventListener('dashboard:navigate', update);
    return () => {
      removeEventListener('popstate', update);
      removeEventListener('dashboard:navigate', update);
    };
  }, []);
  return path;
}

export function navigate(to: string): void {
  if (to === location.pathname + location.search) return;
  history.pushState(null, '', to);
  dispatchEvent(new Event('dashboard:navigate'));
  scrollTo(0, 0);
}

/** A link inside the dashboard that doesn't reload the page. */
export function Link(props: { href: string; class?: string; children: ComponentChildren; 'aria-current'?: 'page' }) {
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(props.href);
  };
  return (
    <a href={props.href} class={props.class} onClick={onClick} aria-current={props['aria-current']}>
      {props.children}
    </a>
  );
}

// ---- formatting ----

export function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

export function fmtDate(ts: number, withTime = true): string {
  return new Date(ts).toLocaleString(undefined, withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' });
}

export function fmtAgo(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return fmtDate(ts, false);
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// ---- components ----

export function Card(props: { title?: ComponentChildren; actions?: ComponentChildren; children: ComponentChildren; class?: string }) {
  return (
    <section class={`card ${props.class ?? ''}`}>
      {(props.title || props.actions) && (
        <header class="card-head">
          {props.title && <h2>{props.title}</h2>}
          {props.actions && <div class="card-actions">{props.actions}</div>}
        </header>
      )}
      {props.children}
    </section>
  );
}

export function PremiumBadge(props: { href?: string }) {
  const badge = <span class="badge premium">Premium</span>;
  return props.href ? <Link href={props.href}>{badge}</Link> : badge;
}

export function PlanBadge(props: { plan: 'free' | 'premium' | null }) {
  if (!props.plan) return null;
  return props.plan === 'premium' ? <span class="badge premium">Premium</span> : <span class="badge">Free</span>;
}

export function Toggle(props: { checked: boolean; disabled?: boolean; label: string; onChange: (on: boolean) => void }) {
  return (
    <label class={`switch ${props.disabled ? 'disabled' : ''}`} title={props.label}>
      <input
        type="checkbox"
        role="switch"
        checked={props.checked}
        disabled={props.disabled}
        aria-label={props.label}
        onChange={(e) => props.onChange((e.currentTarget as HTMLInputElement).checked)}
      />
      <span class="track" aria-hidden="true" />
    </label>
  );
}

export function Field(props: { label: string; hint?: ComponentChildren; children: ComponentChildren; locked?: string }) {
  return (
    <div class="setting">
      <div class="setting-text">
        <div class="setting-label">
          {props.label} {props.locked && <PremiumBadge href={props.locked} />}
        </div>
        {props.hint && <div class="setting-hint">{props.hint}</div>}
      </div>
      <div class="setting-control">{props.children}</div>
    </div>
  );
}

export function NumberInput(props: { value: number; min: number; max: number; unit: string; disabled?: boolean; onChange: (n: number) => void }) {
  const onInput = (e: JSX.TargetedEvent<HTMLInputElement>) => {
    const n = Math.round(Number(e.currentTarget.value));
    if (Number.isFinite(n)) props.onChange(n);
  };
  return (
    <span class="number">
      <input type="number" inputMode="numeric" min={props.min} max={props.max} value={props.value} disabled={props.disabled} onInput={onInput} />
      <span class="muted">{props.unit}</span>
    </span>
  );
}

export function Spinner(props: { label?: string }) {
  return (
    <div class="loading" role="status">
      <span class="spinner" aria-hidden="true" />
      {props.label ?? 'Loading…'}
    </div>
  );
}

export function Notice(props: { kind?: 'info' | 'error' | 'success' | 'warn'; children: ComponentChildren }) {
  return (
    <div class={`notice ${props.kind ?? 'info'}`} role={props.kind === 'error' ? 'alert' : 'status'}>
      {props.children}
    </div>
  );
}

export function GuildIcon(props: { name: string; iconUrl: string | null; size?: number }) {
  const size = props.size ?? 40;
  if (props.iconUrl) return <img class="guild-icon" src={props.iconUrl} alt="" width={size} height={size} referrerPolicy="no-referrer" />;
  const initials = props.name
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 3);
  return (
    <span class="guild-icon initials" style={{ width: `${size}px`, height: `${size}px` }} aria-hidden="true">
      {initials}
    </span>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** A Discord profile picture, or the person's initials if there's none or it no longer loads. */
export function Avatar(props: { name: string; url: string | null; size?: number }) {
  const [broken, setBroken] = useState(false);
  const size = props.size ?? 32;
  useEffect(() => setBroken(false), [props.url]);
  const style = { width: `${size}px`, height: `${size}px` };
  if (props.url && !broken) {
    return <img class="avatar" src={props.url} alt={props.name} width={size} height={size} style={style} loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  }
  return (
    <span class="avatar initials" style={style} role="img" aria-label={props.name}>
      {initials(props.name)}
    </span>
  );
}

/** Profile pictures of the people in a clip; hovering (or focusing) one shows the name. Quiet ones are faded. */
export function People(props: { people: { userId: string; name: string; avatarUrl: string | null; spoke: boolean }[]; size?: number; label?: string }) {
  if (props.people.length === 0) return null;
  return (
    <ul class="people" aria-label={props.label ?? 'In the call'}>
      {props.people.map((p) => (
        <li key={p.userId} class={`person${p.spoke ? '' : ' quiet'}`} data-name={p.spoke ? p.name : `${p.name} (didn't speak)`} tabIndex={0}>
          <Avatar name={p.name} url={p.avatarUrl} size={props.size} />
        </li>
      ))}
    </ul>
  );
}

/** A short-lived message after saving. */
export function useToast(): [string | null, (msg: string) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 2500);
    return () => clearTimeout(t);
  }, [msg]);
  return [msg, setMsg];
}
